import {
    SurfaceAreaType,
    SurfaceFoldLineType,
    SurfaceGroupType,
    SurfaceResponseAdditionalDataType,
    SurfaceResponseType,
    SurfaceType,
} from 'src/types/surface';
import { resolveEffectiveSku } from 'src/util/skuKey';
import { getSdk } from 'src/sdk';
import type { Explorer_CalculatedSurfaceType } from '@rendering/web-client';

// Only the first mask of the given type that has paths is used
function parseClipArea(docAdditionalData: SurfaceResponseAdditionalDataType, type: string) {
    const mask = docAdditionalData?.masks?.find((m) => m.pathType === type && m.paths?.length > 0);
    if (!mask) {
        return;
    }

    return mask.paths.flatMap((maskPath): SurfaceAreaType[] => {
        if (maskPath.pathPoints?.length === 3) {
            return [
                {
                    x: maskPath.anchorX,
                    y: maskPath.anchorY,
                    anchorX: maskPath.anchorX,
                    anchorY: maskPath.anchorY,
                    width: maskPath.pathPoints[1].endPointX - maskPath.anchorX,
                    height: maskPath.pathPoints[1].endPointY - maskPath.anchorY,
                    pathPoints: maskPath.pathPoints,
                },
            ];
        }
        // Not a 3-point path: fall back to the bounding rectangle (position replaces anchor, but anchor is still needed)
        if (mask.boundingArea?.type !== 'Rectangle') {
            return [];
        }
        const { x, y, width, height } = mask.boundingArea.position;
        return [
            {
                x,
                y,
                width,
                height,
                pathPoints: maskPath.pathPoints,
                anchorX: maskPath.anchorX,
                anchorY: maskPath.anchorY,
            },
        ];
    });
}

function parseFoldLines(docAdditionalData: SurfaceResponseAdditionalDataType) {
    if (!docAdditionalData?.guides || docAdditionalData.guides.length === 0) {
        return;
    }
    const fold: SurfaceFoldLineType[] = [];
    for (const element of docAdditionalData.guides) {
        const guideElement = element;

        if (guideElement.pathType === 'FOLD' && guideElement.paths && guideElement.paths.length !== 0) {
            guideElement.paths.forEach((path) => {
                // straight fold lines
                if (
                    path.pathPoints?.length === 1 &&
                    !path.pathPoints[0].firstControlPointX &&
                    !path.pathPoints[0].secondControlPointX
                ) {
                    fold.push({
                        startX: path.anchorX,
                        startY: path.anchorY,
                        endX: path.pathPoints[0].endPointX,
                        endY: path.pathPoints[0].endPointY,
                    });
                }
            });
        }
    }

    return fold; // eslint-disable-line consistent-return
}

function mapSurface(surface: SurfaceResponseType) {
    const fullBleedAreas = parseClipArea(surface.docAdditionalData, 'BLEED') || [];
    const trimAreas = parseClipArea(surface.docAdditionalData, 'TRIM') || [];
    let fullBleedArea: SurfaceType['fullBleedArea'];

    if (!fullBleedAreas || fullBleedAreas.length === 0) {
        fullBleedArea = {
            x: 0,
            y: 0,
            width: surface.widthCm,
            height: surface.heightCm,
        };
    } else {
        fullBleedArea = fullBleedAreas[0];
    }

    return {
        id: surface.id,
        name: surface.location?.name || surface.name,
        panelName: surface.name,
        locationName: surface.location?.name,
        technology: surface.processType,
        trimArea: trimAreas.length > 0 ? trimAreas[0] : ({} as SurfaceAreaType), // Principal TrimArea use as default
        fullBleedArea, // Principal FullBleedArea us as default
        foldLines: parseFoldLines(surface.docAdditionalData),
        width: surface.widthCm,
        height: surface.heightCm,
        trimAreas, // All trim areas use for automasking
        fullBleedAreas, // All fullBleedAreas use for automasking
    };
}

function mapSurfaceGroup(group: SurfaceGroupType) {
    return {
        id: group.id,
        surfaces: group.surfaces.map(mapSurface),
    };
}

function parseProductResponse(response: Explorer_CalculatedSurfaceType) {
    // Validate the surface spec contains at least one surface group
    if (response && response.surfaceGroups.length > 0) {
        // Validate each surface group contains non-empty surfaces
        for (const element of response.surfaceGroups) {
            if (element.surfaces.length === 0) {
                throw new Error('An empty surface was found in the surface specification!');
            }
        }

        // SDK types mark masks/paths optional; mapping code already guards with `?.`
        return (response.surfaceGroups as SurfaceGroupType[]).map(mapSurfaceGroup);
    } else {
        throw new Error('No surface groups found in the surface specification!');
    }
}

/**
 * Retrieves SKU information from the surface specifications service.
 * @param sku - The product SKU.
 * @param variables - Optional product variables.
 * @returns - A promise of the product information.
 */
export function retrieveSurfaceData(sku: string, skuVersion: number | undefined, variables = {}) {
    if (!sku) {
        return Promise.reject(new Error(`retrieveSurfaceData requires a sku, got: sku=${sku}`));
    }
    const skuSegment = skuVersion === undefined ? sku : `${sku}/${skuVersion}`;
    return getSdk().Products.retrieveSurfaceData(skuSegment, variables).then(parseProductResponse);
}

type RetrieveSurfaceParams = {
    sku: string;
    skuVersion?: number;
    variables?: Record<string, unknown>;
    merchantSku?: string;
    merchantVersion?: number;
    merchantVariables?: Record<string, unknown>;
};

/**
 * Resolves surface data using merchant SKU (PRD) when available, falling back to the fulfiller SKU (CIM).
 * Surface Explorer accepts both PRD and CIM SKUs — PRD returns merchant-configured surfaces.
 */
export function retrieveSurfaceDataForProduct({
    sku,
    skuVersion,
    variables,
    merchantSku,
    merchantVersion,
    merchantVariables,
}: RetrieveSurfaceParams) {
    const { effectiveSku, effectiveVersion } = resolveEffectiveSku({ sku, skuVersion, merchantSku, merchantVersion });
    // Variables are selected to match the resolved SKU: merchant variables accompany a merchant
    // SKU, and fulfiller variables accompany a fulfiller SKU.
    const effectiveVariables = merchantSku ? merchantVariables : variables;
    return retrieveSurfaceData(effectiveSku, effectiveVersion, effectiveVariables);
}

/**
 * Retrieves required variable attributes from the surface specifications service.
 * @param sku - The product SKU (CIM or PRD).
 * @param version - Optional product version.
 * @returns - A promise of the required variable names.
 */
export function retrieveRequiredVariables(sku: string, version: number) {
    return getSdk()
        .Products.retrieveRequiredVariables(sku, version)
        .then(
            (res) => res ?? [],
            () => undefined,
        );
}
